import { type ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import type { TopAdvisor } from '@/hooks/api/useAnalytics';
import { formatCurrencyCompact, formatNumber } from '@/lib/formatters';

interface Props {
  data: TopAdvisor[];
}

const columns: ColumnDef<TopAdvisor, unknown>[] = [
  {
    id: 'rank',
    header: '#',
    cell: ({ row }) => <span className="font-tabular text-muted-foreground">{row.index + 1}</span>,
    size: 40,
  },
  {
    accessorKey: 'agentName',
    header: 'Advisor',
    cell: ({ row }) => (
      <div className="min-w-0">
        <TruncatedTooltip
          text={row.original.agentName || row.original.advisorId}
          className="font-medium"
        />
        {row.original.agentName && (
          <TruncatedTooltip
            text={row.original.advisorId}
            className="text-xs text-muted-foreground"
          />
        )}
      </div>
    ),
  },
  {
    accessorKey: 'opportunityCount',
    header: 'Opportunities',
    cell: ({ row }) => (
      <span className="font-tabular">{formatNumber(row.original.opportunityCount)}</span>
    ),
  },
  {
    accessorKey: 'avgScore',
    header: 'Avg Score',
    cell: ({ row }) => {
      const score = Math.round(row.original.avgScore);
      const variant = score >= 80 ? 'success' : score >= 50 ? 'warning' : 'secondary';
      return (
        <Badge variant={variant} className="font-tabular">
          {score}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'totalEstimatedValue',
    header: 'Total Est. Value',
    cell: ({ row }) => (
      <span className="font-tabular font-medium">
        {formatCurrencyCompact(row.original.totalEstimatedValue)}
      </span>
    ),
  },
];

export function TopAdvisorsTable({ data }: Props) {
  return (
    <Card style={{ boxShadow: 'var(--inset-shadow)' }}>
      <CardHeader>
        <CardTitle className="text-sm">Top Advisors</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <DataTable
          columns={columns}
          data={data}
          density="compact"
          emptyMessage="No advisor data available"
        />
      </CardContent>
    </Card>
  );
}
